import React, { FC } from 'react';
import { formatDistanceStrict } from 'date-fns';
import classnames from 'classnames';

import PremiumRibbonIcon from 'components/Icons/PremiumRibbon';

import styles from './FeedCard.module.scss';

interface Category {
  name: string;
  link?: string;
  url?: string;
}

interface FeedCardProps {
  category?: Category | null;
  createdAt: number;
  displayTitle?: string | null;
  fullWidthContent?: boolean;
  hideSubtitle?: boolean;
  imageUrl?: string | null;
  isPremium?: boolean;
  newWindow?: boolean;
  onClickLink: () => void;
  onClickTag: (category: Category) => void;
  permalink: string;
  showTimestamp?: boolean;
  subtitle: string;
  title: string;
}

const getContentClassnames = (fullWidthContent: boolean) =>
  classnames({
    'quiver-feed-card__content': true,
    'quiver-feed-card__content__fullwidth': fullWidthContent,
  });

const formatFeedCardDate = (date: number) => {
  let cardDate = '';
  const formattedDate = formatDistanceStrict(date * 1000, new Date()).split(' ');
  if (formattedDate && formattedDate.length && formattedDate.length > 1) {
    cardDate = `${formattedDate[0]} ${formattedDate[1]} ago`;
  }
  return cardDate;
};

const FeedCard: FC<FeedCardProps> = ({
  category,
  createdAt,
  displayTitle,
  fullWidthContent,
  hideSubtitle,
  imageUrl,
  isPremium,
  newWindow,
  onClickLink,
  onClickTag,
  permalink,
  showTimestamp,
  subtitle,
  title,
}) => (
  <div className={classnames(styles.feedCard, 'quiver-feed-card')}>
    {isPremium ? <PremiumRibbonIcon /> : null}
    <div className="quiver-feed-card__image">
      <a
        tabIndex={0}
        href={permalink}
        onClick={onClickLink}
        aria-label="Feed Card Image"
        style={{ backgroundImage: `url(${imageUrl})` }}
        target={newWindow ? '_blank' : undefined}
        rel="noreferrer"
      />
    </div>
    <div className={getContentClassnames(fullWidthContent!)}>
      <a
        className="quiver-feed-card__title"
        href={permalink}
        target={newWindow ? '_blank' : undefined}
        onClick={onClickLink}
        rel="noreferrer"
      >
        <h3>{displayTitle || title}</h3>
      </a>
      {!hideSubtitle ? <p className="quiver-feed-card__subtitle">{subtitle}</p> : null}
      <div className="quiver-feed-card__content__bottom">
        {category ? (
          <a
            className="quiver-feed-card__category"
            href={category?.url ?? category?.link}
            onClick={() => onClickTag(category)}
          >
            {category.name}
          </a>
        ) : null}
        {showTimestamp ? (
          <span className="quiver-feed-card__date" data-testid="feed-card-date">
            {formatFeedCardDate(createdAt)}
          </span>
        ) : null}
      </div>
    </div>
  </div>
);

export default FeedCard;
